import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import telegramIcon from '../../assets/icons/telegramIcon.png';
import vkontakteIcon from '../../assets/icons/vkontakteIcon.png';
import s from './Footer.module.css';
import { settingsApi } from '../../api/settings';
import { PrivacyPolicy } from '../PrivacyPolicy/PrivacyPolicy';
import { ConsentModal } from '../ConsentModal/ConsentModal';

interface Contact {
    id: number;
    phone: string;
    name: string;
}

interface ContentBlock {
    type: 'paragraph' | 'image';
    content: string;
    src?: string;
    alt?: string;
}

interface SettingsData {
    address: string;
    email: string;
    contacts: Contact[];
    privacyPolicy: string;
    consentData: string;
}

export const Footer: React.FC = () => {
    const [settings, setSettings] = useState<SettingsData>({
        address: '',
        email: '',
        contacts: [{ id: 1, phone: '', name: '' }],
        privacyPolicy: '',
        consentData: '',
    });
    const [loading, setLoading] = useState(true);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showConsent, setShowConsent] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await settingsApi.getAllPublic();

            const settingsMap: any = {};
            data.forEach(item => {
                settingsMap[item.key] = item.value;
            });

            let contacts: Contact[] = [{ id: 1, phone: '', name: '' }];
            if (settingsMap.contacts) {
                try {
                    contacts = JSON.parse(settingsMap.contacts);
                } catch {
                    contacts = [{ id: 1, phone: '', name: '' }];
                }
            }

            setSettings({
                address: settingsMap.office_address || 'Хабаровск, Калинина 120',
                email: settingsMap.contact_email || 'rdb-method@yandex.ru',
                contacts: contacts,
                privacyPolicy: settingsMap.privacy_policy_text || '',
                consentData: settingsMap.consent_data_text || '',
            });
        } catch (error) {
            console.error('Ошибка загрузки настроек:', error);
        } finally {
            setLoading(false);
        }
    };

    const firstContact = settings.contacts?.[0] || { phone: '', name: '' };

    const renderContent = (content: string) => {
        if (!content) return <p>Нет содержимого</p>;

        try {
            const parsed = JSON.parse(content);
            if (Array.isArray(parsed)) {
                return parsed.map((block: ContentBlock, index: number) => {
                    if (block.type === 'paragraph') {
                        const htmlContent = block.content || '';
                        const parts = htmlContent.split('<br />');
                        if (parts.length > 1) {
                            return (
                                <p key={index}>
                                    {parts.map((part: string, i: number) => (
                                        <React.Fragment key={i}>
                                            <span dangerouslySetInnerHTML={{ __html: part }} />
                                            {i < parts.length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </p>
                            );
                        }
                        return <p key={index} dangerouslySetInnerHTML={{ __html: htmlContent }} />;
                    }
                    if (block.type === 'image') {
                        return (
                            <div key={index} className={s.previewImage}>
                                🖼️ Изображение: {block.alt || 'без описания'}
                            </div>
                        );
                    }
                    return null;
                });
            }
            const htmlContent = content;
            const parts = htmlContent.split('<br />');
            if (parts.length > 1) {
                return (
                    <p>
                        {parts.map((part: string, i: number) => (
                            <React.Fragment key={i}>
                                <span dangerouslySetInnerHTML={{ __html: part }} />
                                {i < parts.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </p>
                );
            }
            return <p dangerouslySetInnerHTML={{ __html: htmlContent }} />;
        } catch {
            const text = content;
            const lines = text.split('\n');
            if (lines.length > 1) {
                return (
                    <div>
                        {lines.map((line: string, i: number) => (
                            <p key={i} style={{ margin: '4px 0' }}>
                                {line || '\u00A0'}
                            </p>
                        ))}
                    </div>
                );
            }
            return <p>{text}</p>;
        }
    };

    if (loading) {
        return <div className={s.footerLoading}>Загрузка...</div>;
    }

    return (
        <>
            <div className={s.footer}>
                <div className={s.footerContactBlock}>
                    <h5 className={s.footerTitleRight}>Связаться</h5>
                    <div className={s.rightPosition}>
                        <a href={`mailto:${settings.email}`} className={s.footerEmail}>
                            {settings.email}
                        </a>
                    </div>
                </div>

                <div className={s.footerTopRow}>
                    <div className={s.footerOffice}>
                        <h5>Офис</h5>
                        <div className={s.footerText}>
                            {settings.address.split(',').map((part, index) => (
                                <React.Fragment key={index}>
                                    {part.trim()}
                                    {index < settings.address.split(',').length - 1 && <br />}
                                </React.Fragment>
                            ))}
                        </div>
                        <div className={s.footerCopy}>© Все права защищены</div>
                    </div>

                    <div className={s.footerNav}>
                        <h5>Навигация</h5>
                        <Link to="/" className={s.footerLink}>Проекты</Link>
                        <Link to="/contacts" className={s.footerLink}>Контакты</Link>
                    </div>
                </div>

                <div className={s.footerContactsBlock}>
                    <h5>Контакты</h5>
                    <a href={`tel:${firstContact.phone.replace(/[^0-9+]/g, '')}`} className={s.footerPhone}>
                        {firstContact.phone}
                    </a>
                    <div className={s.footerText}>{firstContact.name}</div>
                    <div className={s.socialIcons}>
                        <a
                            href="https://t.me/rdb_method"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img src={telegramIcon} alt="Telegram" width={24} height={24} />
                        </a>
                        <a
                            href="https://vk.com/rdb.method?from=groups"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img src={vkontakteIcon} alt="Vk" width={24} height={24} />
                        </a>
                    </div>
                </div>

                <div className={s.footerLegalBlock}>
                    <span
                        className={s.footerLegal}
                        onClick={() => setShowConsent(true)}
                    >
                        Согласие на обработку персональных данных
                    </span>
                    <span
                        className={s.footerLegal}
                        onClick={() => setShowPrivacy(true)}
                    >
                        Политика конфиденциальности
                    </span>
                </div>
            </div>

            <ConsentModal
                isOpen={showConsent}
                onClose={() => setShowConsent(false)}
                content={settings.consentData}
                renderContent={renderContent}
            />
            <PrivacyPolicy
                isOpen={showPrivacy}
                onClose={() => setShowPrivacy(false)}
                content={settings.privacyPolicy}
                renderContent={renderContent}
            />
        </>
    );
};